import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useImpersonation } from "@/contexts/ImpersonationContext";
import { usePermissions } from "@/hooks/use-permissions";
import type { ProjectRolePermission } from "@/hooks/use-project-role-permissions";
import {
  PROJECT_PERMISSION_KEYS,
  VIEWER_FLOOR_KEYS,
  hasPermission,
  type ProjectPermissionKey,
  type ProjectRoleKey,
} from "@/lib/project-permissions";

export type { ProjectPermissionKey, ProjectRoleKey };

function emptyPermissionMap(value: boolean): Record<ProjectPermissionKey, boolean> {
  return PROJECT_PERMISSION_KEYS.reduce<Record<ProjectPermissionKey, boolean>>(
    (acc, key) => {
      acc[key] = value;
      return acc;
    },
    {} as Record<ProjectPermissionKey, boolean>,
  );
}

interface ProjectPermissionData {
  /** Roles the active user has on this project (project_lead / client_advisor / sales / assignee). */
  projectRoles: ProjectRoleKey[];
  /** Permission rows from project_role_permissions filtered to relevant roles. */
  perms: ProjectRolePermission[];
}

/**
 * Henter prosjekt-rolle og permission-rader for én bruker på ett prosjekt.
 *
 * Brukes internt av `useProjectPermission` og `useProjectPermissions`.
 * Cache key: `["project-permission", projectId, userId]`, staleTime 5 min.
 */
function useProjectPermissionData(
  projectId: string | null | undefined,
  userId: string | null | undefined,
  enabled: boolean,
) {
  return useQuery({
    queryKey: ["project-permission", projectId ?? null, userId ?? null],
    enabled: enabled && !!projectId && !!userId,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<ProjectPermissionData> => {
      if (!projectId || !userId) {
        return { projectRoles: [], perms: [] };
      }

      // Level 2: prosjekt-roller via projects + project_owners
      const projectRoles: ProjectRoleKey[] = [];

      const { data: project, error: projectError } = await supabase
        .from("projects")
        .select("production_lead_id, advisor_id")
        .eq("id", projectId)
        .maybeSingle();
      if (projectError) throw projectError;

      if (project?.production_lead_id === userId) projectRoles.push("project_lead");
      if (project?.advisor_id === userId) projectRoles.push("client_advisor");

      const { data: owners, error: ownersError } = await supabase
        .from("project_owners")
        .select("user_id")
        .eq("project_id", projectId)
        .eq("user_id", userId);
      if (ownersError) throw ownersError;
      if ((owners ?? []).length > 0) projectRoles.push("sales");

      // Level 3: assignment-basert. Bruker join på budget_lines for å filtrere
      // assignments til kun de som tilhører dette prosjektet. PostgREST krever
      // !inner for å filtrere på joinet ressurs.
      const { data: assignments, error: assignmentsError } = await supabase
        .from("budget_line_assignments")
        .select("id, budget_lines!inner(project_id)")
        .eq("user_id", userId)
        .eq("budget_lines.project_id", projectId)
        .limit(1);
      if (assignmentsError) throw assignmentsError;
      if ((assignments ?? []).length > 0) projectRoles.push("assignee");

      if (projectRoles.length === 0) {
        return { projectRoles, perms: [] };
      }

      const { data: perms, error: permsError } = await supabase
        .from("project_role_permissions")
        .select("role, permission_key, granted")
        .in("role", projectRoles);
      if (permsError) throw permsError;

      return {
        projectRoles,
        perms: (perms ?? []) as ProjectRolePermission[],
      };
    },
  });
}

/** Sett av innvilgede nøkler på tvers av brukerens prosjekt-roller. */
function grantedSet(perms: ProjectRolePermission[]): Set<ProjectPermissionKey> {
  return new Set(perms.filter((p) => p.granted).map((p) => p.permission_key));
}

/**
 * Returner `true` hvis NOEN av brukerens prosjekt-roller gir gitt key, med
 * edit→view-implikasjon (VIB-1164): en `view_*`-nøkkel er innvilget hvis den
 * paret `edit_*`-nøkkelen er innvilget («Endre forutsetter Se»).
 */
function resolveGranted(
  perms: ProjectRolePermission[],
  key: ProjectPermissionKey,
): boolean {
  return hasPermission(grantedSet(perms), key);
}

/**
 * Bygg full permission-map fra hentet permission-data, med edit→view-
 * implikasjon for alle data-nøkler.
 */
function buildPermissionMap(
  perms: ProjectRolePermission[],
): Record<ProjectPermissionKey, boolean> {
  const set = grantedSet(perms);
  const map = emptyPermissionMap(false);
  for (const key of PROJECT_PERMISSION_KEYS) {
    map[key] = hasPermission(set, key);
  }
  return map;
}

/**
 * 3-nivå permission-sjekk for én key på ett prosjekt.
 *
 * - **Level 1 (system-rolle):** admin/superadmin → alltid `true`.
 * - **Level 2 (prosjekt-rolle):** project_lead / client_advisor / sales fra
 *   `projects.production_lead_id`, `projects.advisor_id` og `project_owners`.
 * - **Level 3 (assignment):** `assignee` hvis user har minst én rad i
 *   `budget_line_assignments` for prosjektet.
 *
 * Returnerer `true` hvis NOEN av rollene har `granted=true` for key.
 */
export function useProjectPermission(
  projectId: string | null | undefined,
  key: ProjectPermissionKey,
): boolean {
  const { activeUser } = useImpersonation();
  const { isAdmin, isSuperadmin } = usePermissions();
  const userId = activeUser?.id ?? null;
  const systemOverride = isAdmin || isSuperadmin;

  // useQuery kalles uansett, men disables når systemOverride er true så vi
  // ikke gjør unødvendige DB-kall for admins.
  const { data } = useProjectPermissionData(projectId, userId, !systemOverride);

  if (systemOverride) return true;
  if (!userId || !projectId) return false;
  // Viewer-floor: ingen rolle på prosjektet, men innlogget ansatt får
  // lesetilgang likevel.
  const hasNoProjectRole = (data?.projectRoles.length ?? 0) === 0;
  if (hasNoProjectRole && VIEWER_FLOOR_KEYS.includes(key)) return true;
  return resolveGranted(data?.perms ?? [], key);
}

/**
 * Returnerer hele permission-mappet (alle 17 keys → bool) for komponenter
 * som trenger mange permissions samtidig (f.eks. ProjectDetailContext).
 *
 * Samme 3-nivå-resolution som `useProjectPermission`.
 */
export function useProjectPermissions(
  projectId: string | null | undefined,
): Record<ProjectPermissionKey, boolean> {
  const { activeUser } = useImpersonation();
  const { isAdmin, isSuperadmin } = usePermissions();
  const userId = activeUser?.id ?? null;
  const systemOverride = isAdmin || isSuperadmin;

  const { data } = useProjectPermissionData(projectId, userId, !systemOverride);

  return useMemo(() => {
    if (systemOverride) return emptyPermissionMap(true);
    const map = buildPermissionMap(data?.perms ?? []);
    if (!userId) return map;
    const hasNoProjectRole = (data?.projectRoles.length ?? 0) === 0;
    if (hasNoProjectRole) {
      for (const k of VIEWER_FLOOR_KEYS) map[k] = true;
    }
    return map;
  }, [systemOverride, data, userId]);
}
